import { useState } from "react";
import { deleteSensor } from "../../../Services/Admin/Sensors";

type DeleteSensorButtonProps = {
  sensorId: string;
  /** Namnet i bekräftelsen. Faller tillbaka på sensorId. */
  name?: string | null;
  disabled?: boolean;
  onDeleted: (result: { sensorId: string; credentialRevoked: boolean }) => void;
  onError: (message: string) => void;
};

/**
 * Radering i två steg.
 *
 * Steg ett öppnar rutan, steg två utför. En window.confirm hade räckt
 * för ett ja/nej, men inte för valet som spelar roll: om lådans token
 * ska spärras också. Utan spärr dyker sensorn upp som oansluten igen
 * nästa gång den hör av sig, och det ska stå skrivet innan någon
 * trycker — inte upptäckas en kvart senare.
 *
 * Tar sensorId och ett namn i stället för ett sensorobjekt: knappen
 * sitter i tre listor som alla har sin egen form på sensorraden.
 */
export default function DeleteSensorButton({
  sensorId,
  name,
  disabled,
  onDeleted,
  onError,
}: DeleteSensorButtonProps) {
  const [open, setOpen] = useState(false);
  const [revokeCredential, setRevokeCredential] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const result = await deleteSensor(sensorId, { revokeCredential });
      setOpen(false);
      onDeleted({ sensorId, credentialRevoked: result.credentialRevoked });
    } catch (e: any) {
      onError(e?.message ?? "Could not delete the sensor");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="admin-btn--danger"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        Delete sensor
      </button>
    );
  }

  return (
    <div className="sensor-confirm">
      <p className="sensor-confirm-text">
        Delete <strong>{name || sensorId}</strong>? Its telemetry will also be
        deleted. Venue playback history is retained.
      </p>

      <label className="sensor-toggle">
        <input
          type="checkbox"
          checked={revokeCredential}
          disabled={busy}
          onChange={(event) => setRevokeCredential(event.target.checked)}
        />
        Revoke the device credential too
      </label>

      <p className="sensor-dim">
        {revokeCredential
          ? "The device cannot return without being enrolled again."
          : "The device will register as unclaimed the next time it reports in."}
      </p>

      <div className="sensor-confirm-actions">
        <button
          type="button"
          className="admin-btn--danger"
          disabled={busy}
          onClick={confirm}
        >
          {busy ? "Deleting…" : "Yes, delete"}
        </button>
        <button type="button" disabled={busy} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
